/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useState } from 'react'
import MainLayout from '../../../Components/MainLayout'
import {
  Table,
  Tooltip,
  Space,
  Breadcrumb,
  Card,
  Button,
  Popconfirm,
  Tabs,
} from 'antd'
import { Link, useParams } from 'react-router-dom'
import { PlusOutlined, FileAddOutlined, FileOutlined } from '@ant-design/icons'
import { useLesson } from '../../../hooks'
import LessonForm from '../LessonForm'
import DocumentForm from '../DocumentForm'

const { TabPane } = Tabs

function Lesson({ user }) {
  const { id } = useParams()
  const {
    getCourse,
    loading,
    addLesson,
    updateLesson,
    deleteLesson,
    addDocument,
    updateDocument,
    deleteDocument,
  } = useLesson()
  const [course, setCourse] = useState()
  const [lessonDialog, setLessonDialog] = useState({
    open: false,
    data: null,
  })
  const [docDialog, setDocDialog] = useState({
    open: false,
    data: null,
  })

  useEffect(() => {
    if (user?.uid && id) {
      fetchData(id)
    }
  }, [user, id])

  const fetchData = async (id) => {
    const result = await getCourse(id)
    setCourse(result)
  }

  const onCreate = async (data) => {
    const result = await addLesson(id, data)
    if (result) {
      fetchData(id)
      setLessonDialog({
        open: false,
        data: null,
      })
    }
  }

  const onEdit = async (lessonId, data) => {
    const result = await updateLesson(id, lessonId, data)
    if (result === 'success') {
      fetchData(id)
      setLessonDialog({
        open: false,
        data: null,
      })
    }
  }

  const onDelete = async (lessonId) => {
    const result = await deleteLesson(id, lessonId)
    if (result === 'success') {
      fetchData(id)
    }
  }

  const onCreateDoc = async (data) => {
    const result = await addDocument(id, data)
    if (result) {
      fetchData(id)
      setDocDialog({
        open: false,
        data: null,
      })
    }
  }

  const onEditDoc = async (docId, data) => {
    const result = await updateDocument(id, docId, data)
    if (result === 'success') {
      fetchData(id)
      setDocDialog({
        open: false,
        data: null,
      })
    }
  }

  const onDeleteDoc = async (docId) => {
    const result = await deleteDocument(id, docId)
    if (result === 'success') {
      fetchData(id)
    }
  }

  const columns = [
    {
      title: 'Order',
      key: 'order',
      dataIndex: 'order',
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text) => <Button type="link">{text}</Button>,
    },
    {
      title: 'Minutes',
      dataIndex: 'minutes',
      key: 'minutes',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      onCell: () => {
        return {
          style: {
            whiteSpace: 'nowrap',
            maxWidth: 150,
          },
        }
      },
      render: (text) => (
        <Tooltip title={text}>
          <div style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
            {text}
          </div>
        </Tooltip>
      ),
    },

    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Space size="middle">
          <Button
            type="link"
            onClick={() =>
              setLessonDialog({
                open: true,
                data: record,
              })
            }
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure to delete this Lesson?"
            onConfirm={() => onDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link">Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const docColumns = [
    {
      title: 'Order',
      key: 'order',
      dataIndex: 'order',
    },
    {
      title: 'Title',
      key: 'title',
      dataIndex: 'title',
    },
    {
      title: 'File',
      key: 'file',
      dataIndex: 'file',
      render: (text, record) => (
        <a href={text} target="_blank" rel="noreferrer">
          <FileOutlined /> View
        </a>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Space size="middle">
          <Button
            type="link"
            onClick={() =>
              setDocDialog({
                open: true,
                data: record,
              })
            }
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure to delete this document?"
            onConfirm={() => onDeleteDoc(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link">Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <MainLayout user={user}>
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item>
          <Link to="/">Home</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to="/onlineclass">Online Class</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>{course?.header}</Breadcrumb.Item>
      </Breadcrumb>
      <Card
        title={course?.header}
        extra={
          <>
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                setLessonDialog({
                  open: true,
                  data: null,
                })
              }}
            >
              New Lesson
            </Button>
            &nbsp;
            <Button
              icon={<FileAddOutlined />}
              onClick={() => {
                setDocDialog({
                  open: true,
                  data: null,
                })
              }}
            >
              Document
            </Button>
          </>
        }
      >
        <Tabs type="card">
          <TabPane tab="Lesson" key="1">
            <Table
              scroll={{ x: 'max-content' }}
              rowKey={(record) => `${record.id}`}
              columns={columns}
              dataSource={course?.lessons}
              loading={loading}
            />
          </TabPane>
          <TabPane tab="Documents" key="2">
            <Table
              scroll={{ x: 'max-content' }}
              rowKey={(record) => `${record.id}`}
              columns={docColumns}
              dataSource={course?.documents}
              loading={loading}
            />
          </TabPane>
        </Tabs>
      </Card>
      {lessonDialog.open && (
        <LessonForm
          visible={lessonDialog.open}
          onClose={() =>
            setLessonDialog({
              open: false,
              data: null,
            })
          }
          data={lessonDialog.data}
          onEdit={onEdit}
          onCreate={onCreate}
          courseName={course?.header}
        />
      )}
      {docDialog.open && (
        <DocumentForm
          visible={docDialog.open}
          onClose={() =>
            setDocDialog({
              open: false,
              data: null,
            })
          }
          data={docDialog.data}
          onEdit={onEditDoc}
          onCreate={onCreateDoc}
          courseName={course?.header}
        />
      )}
    </MainLayout>
  )
}

export default Lesson
